import { useEffect, useRef } from "react";

import { getSoilSightProject, getSoilSightScenario } from "services";

import {
  getUserFacingApiErrorMessage,
  productAnalytics,
  useGetPracticeCrops,
  useLocation,
  useMutateSoilSightOutcomes,
  useTranslation,
  useUserContext,
} from "utils";

import { DroppedMainCrop } from "models";

import { Message } from "components";

import { createEmptyYear } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { fromApiCropYear } from "pages/app/scenario-analysis/utils/scenarioApiMappers";
import { classifyForecastError } from "pages/app/scenario-analysis/utils/soilSightTracking";
import { useScenarioOnboarding } from "pages/app/scenario-onboarding/ScenarioOnboardingContext";
import { useGetSoilSightOnboardingSetting } from "pages/app/scenario-onboarding/hooks/useSoilSightOnboardingSetting";
import { isDraftForFarm } from "pages/app/scenario-onboarding/utils/onboardingState";

/**
 * Full-flow restore on re-entry or refresh: with an in-progress draft for the active
 * farm, rebuild the whole flow from the server — baseline rotation, the persisted change
 * scenario, a fresh calculation, and the regenerated recommendation highlights — so a page
 * refresh resumes on the current step instead of restarting at step 1.
 */
export const useOnboardingDraftRestore = () => {
  const { t } = useTranslation("scenarioAnalysis");
  const { user } = useUserContext();
  const { onboardingState, isLoading: isSettingLoading } = useGetSoilSightOnboardingSetting();
  const { data: practiceCrops, isError: isPracticeCropsError } = useGetPracticeCrops({ enabled: true });
  const mutateOutcomes = useMutateSoilSightOutcomes();
  const {
    scenarioRefs,
    setRestoreStatus,
    setSelectedFarmId,
    setSelectedFieldId,
    setCropYears,
    setChangeCropYears,
    setScenarioRefs,
    setImportMeta,
    setOutcomes,
    setCalcStatus,
    persistedProjectIdRef,
  } = useScenarioOnboarding();

  const activeFarmId = user?.farm?.id ?? null;
  const projectId = user?.farm?.soilSightProjectId ?? null;
  // Wait for the practice-crop catalogue: the recommendation highlights are regenerated
  // from it during the restore.
  const shouldRestore = !isSettingLoading && practiceCrops !== undefined && isDraftForFarm(onboardingState, activeFarmId) && Boolean(projectId);

  // Start-once guard + unmount-only cancellation. Deliberately NOT keyed on restoreStatus:
  // setting it to "restoring" inside the effect would flip the dependency and run the
  // cleanup, cancelling the fetch it just started (the restored data was being discarded).
  const hasStartedRef = useRef(false);
  const isUnmountedRef = useRef(false);
  useEffect(
    () => () => {
      isUnmountedRef.current = true;
    },
    []
  );

  const mutateOutcomesRef = useRef(mutateOutcomes);
  mutateOutcomesRef.current = mutateOutcomes;

  // Restore only applies to drafts that existed when the session STARTED — without this disarm the
  // session's own first autosave would qualify and overwrite the rows the user is editing.
  useEffect(() => {
    if (hasStartedRef.current) return;
    const sessionOwnsFlow = Boolean(scenarioRefs.projectId);
    // Boolean(activeFarmId) keeps the disarm from firing on the pre-auth frame where the
    // user context (and thus the disabled settings query) has not resolved yet.
    const settledWithNothingToRestore = !isSettingLoading && Boolean(activeFarmId) && !(isDraftForFarm(onboardingState, activeFarmId) && Boolean(projectId));
    if (sessionOwnsFlow || settledWithNothingToRestore) {
      hasStartedRef.current = true;
      setRestoreStatus("skipped");
    }
  }, [scenarioRefs.projectId, isSettingLoading, onboardingState, activeFarmId, projectId, setRestoreStatus]);

  const importedFieldId = onboardingState?.status === "in-progress" ? onboardingState.importedFieldId : undefined;
  const importBannerDismissed = onboardingState?.status === "in-progress" && onboardingState.importBannerDismissed === true;

  // Crops dropped by a fields-management import arrive via router state — the import response is
  // the only place they exist, so a refresh loses them.
  const location = useLocation();
  const droppedMainCrops = (location.state as { droppedMainCrops?: DroppedMainCrop[] } | null)?.droppedMainCrops ?? [];
  const droppedMainCropsRef = useRef(droppedMainCrops);
  droppedMainCropsRef.current = droppedMainCrops;

  // Step 1 renders no calculation results and its CTA runs a fresh calculation anyway —
  // only a re-entry on a later step needs the (expensive, synchronous) /run during restore.
  const stepIndexRef = useRef<number | null>(null);
  // The route also matches a trailing slash.
  stepIndexRef.current = Number(location.pathname.match(/\/step-(\d)\/?$/)?.[1]) || null;

  // The restore gate waits for the practice-crops catalogue; if that query fails outright,
  // waiting forever would leave the wrapper guard holding a blank page on the later steps —
  // resolve to the error state instead so the guard can bounce to step 1.
  const isRestoreBlockedByCropsError =
    isPracticeCropsError && !isSettingLoading && isDraftForFarm(onboardingState, activeFarmId) && Boolean(projectId) && !hasStartedRef.current;
  useEffect(() => {
    if (!isRestoreBlockedByCropsError) return;
    hasStartedRef.current = true;
    setRestoreStatus("error");
    Message({ content: t("onboarding.restoreError"), icon: "error" });
  }, [isRestoreBlockedByCropsError, setRestoreStatus, t]);

  useEffect(() => {
    if (!shouldRestore || !projectId || hasStartedRef.current) return;
    hasStartedRef.current = true;
    setRestoreStatus("restoring");

    (async () => {
      try {
        const project = await getSoilSightProject(projectId);
        const referenceScenario = project.scenarios.find((scenario) => scenario.isReference) ?? project.scenarios[0];
        if (!referenceScenario) throw new Error("Draft project has no reference scenario");
        const changeScenario = project.scenarios.find((scenario) => !scenario.isReference) ?? null;

        // Both scenario details are independent — fetch them in parallel.
        const [referenceDetail, changeDetail] = await Promise.all([
          getSoilSightScenario(referenceScenario.id),
          changeScenario ? getSoilSightScenario(changeScenario.id) : Promise.resolve(null),
        ]);
        if (isUnmountedRef.current) return;

        const restoredYears = [...referenceDetail.cropYears]
          .sort((a, b) => a.yearIndex - b.yearIndex)
          .map((apiYear, index) => fromApiCropYear(apiYear, `onboarding-y-${index + 1}`));

        // Persisting prunes blank scaffold rows — pad back to the default 4-row editor.
        while (restoredYears.length < 4) {
          restoredYears.push(createEmptyYear(`onboarding-y-${restoredYears.length + 1}`));
        }

        setSelectedFarmId(project.farmId);
        setSelectedFieldId(project.settingsRefinements?.fieldId ?? null);
        setCropYears(restoredYears);
        persistedProjectIdRef.current = projectId;
        setScenarioRefs({
          projectId,
          baselineScenarioId: referenceScenario.id,
          changeScenarioId: changeScenario?.id ?? null,
        });

        if (importedFieldId) {
          setImportMeta({
            importedFieldId,
            droppedMainCrops: droppedMainCropsRef.current,
            bannerDismissed: importBannerDismissed,
            // A field-only import (no rotation in field management) restores as empty rows.
            rotationImported: restoredYears.some((year) => Boolean(year.crop?.trim())),
          });
        }

        // Rebuild the change scenario + highlights so the later steps survive a refresh.
        if (changeScenario && changeDetail) {
          // Ids follow the baseline's yearIndex scheme so the recommendation map and the
          // step-3 baseline/generated lookups align row-by-row.
          const restoredChangeYears = [...changeDetail.cropYears]
            .sort((a, b) => a.yearIndex - b.yearIndex)
            .map((apiYear, index) => fromApiCropYear(apiYear, `onboarding-y-${index + 1}`));
          setChangeCropYears(restoredChangeYears);

          if (stepIndexRef.current === 1) {
            setRestoreStatus("done");
            return;
          }

          setCalcStatus("calculating");
          setRestoreStatus("done");
          try {
            const outcomes = await mutateOutcomesRef.current.mutateAsync(projectId);
            if (isUnmountedRef.current) return;
            setOutcomes(outcomes);
            setCalcStatus("idle");
          } catch (runError) {
            if (isUnmountedRef.current) return;
            setCalcStatus("error");
            productAnalytics.track("soilsights_onboarding:forecast_fail", { step_index: stepIndexRef.current, error_type: classifyForecastError(runError) });
          }
          return;
        }

        setRestoreStatus("done");
      } catch (restoreError) {
        if (isUnmountedRef.current) return;
        // Non-blocking: the user simply starts from an empty step 1.
        setRestoreStatus("error");
        Message({ content: getUserFacingApiErrorMessage(restoreError, t("onboarding.restoreError")), icon: "error" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot restore (hasStartedRef); the other inputs are read once when it starts
  }, [shouldRestore, projectId]);
};
